/**
 * Fonte exportável: Gestão de Chip › Cancelamento
 * -----------------------------------------------------------------------------
 * Reaproveita `get_stock_chip_cancellations` com `p_export_mode = true`
 * (assert_export_allowed + blocos de 500). Usada para enviar a lista ao broker.
 *
 * RELEASE: 2026-10-15 v1.1.0 - Valor mensal registrado no cancelamento (snapshot), origem e data da captura.
 * RELEASE: 2026-10-14 v1.0.0 - Versão inicial.
 */
import { stockRpc } from '@/hooks/useStockMovements';
import { CANCEL_STATUS, PRICE_SOURCE_LABEL, cancelItemsRpcArgs, type CancelItemRow, type CancelItemsFilters } from '@/hooks/useStockChipCancellations';
import { createLogger } from '@/lib/logger';
import { EXPORT_ROW_LIMIT, type ExportSource } from '../types';

const logger = createLogger('ExportChipCancellations');
const LOTE = 500;

export type ChipCancellationsExportParams = Omit<CancelItemsFilters, 'page' | 'pageSize'>;

export const chipCancellationsExportSource: ExportSource<ChipCancellationsExportParams> = {
  id: 'stock_chip_cancellations',
  label: 'Gestão de Chip — Cancelamento',
  moduleKey: 'estoque',
  fileBaseName: 'cancelamento_chips',
  columns: [
    { key: 'iccid', label: 'ICCID', group: 'Chip', defaultSelected: true },
    { key: 'linha', label: 'Linha', group: 'Chip', defaultSelected: true },
    { key: 'imsi', label: 'IMSI', group: 'Chip', defaultSelected: false },
    { key: 'operadora', label: 'Operadora', group: 'Chip', defaultSelected: true },
    { key: 'plano', label: 'Plano de dados', group: 'Chip', defaultSelected: true },
    { key: 'ativacao', label: 'Data de ativação', group: 'Chip', kind: 'date', defaultSelected: false },
    { key: 'broker', label: 'Broker', group: 'Cancelamento', defaultSelected: true },
    { key: 'protocolo', label: 'N. protocolo', group: 'Cancelamento', defaultSelected: true },
    { key: 'envio', label: 'Data de envio', group: 'Cancelamento', kind: 'date', defaultSelected: false },
    { key: 'situacao', label: 'Situação', group: 'Cancelamento', defaultSelected: true },
    { key: 'confirmado_em', label: 'Cancelado em', group: 'Cancelamento', kind: 'date', defaultSelected: false },
    { key: 'valor_mensal', label: 'Valor mensal registrado', group: 'Economia', kind: 'currency', defaultSelected: false },
    { key: 'origem_valor', label: 'Origem do valor', group: 'Economia', defaultSelected: false },
    { key: 'valor_capturado_em', label: 'Valor registrado em', group: 'Economia', kind: 'date', defaultSelected: false },
    { key: 'motivo', label: 'Motivo', group: 'Cancelamento', defaultSelected: false },
  ],
  fetchRows: async (tenantId, p) => {
    const out: CancelItemRow[] = [];
    for (let page = 1; out.length < EXPORT_ROW_LIMIT; page++) {
      const { data, error } = await stockRpc('get_stock_chip_cancellations', {
        ...cancelItemsRpcArgs(p), p_page: page, p_page_size: LOTE, p_export_mode: true, p_tenant_hint: tenantId,
      });
      if (error) {
        logger.error('Falha na exportação de cancelamentos', { error: error.message });
        throw new Error(error.message);
      }
      const items = ((data as { items?: CancelItemRow[] })?.items ?? []);
      out.push(...items);
      if (items.length < LOTE) break;
    }
    return out.slice(0, EXPORT_ROW_LIMIT).map((r) => ({
      iccid: r.iccid, linha: r.line_number, imsi: r.imsi, operadora: r.carrier_name, plano: r.data_plan_name,
      ativacao: r.activated_at, broker: r.supplier_name, protocolo: r.protocol_number, envio: r.sent_at,
      situacao: CANCEL_STATUS[r.status]?.label ?? r.status, confirmado_em: r.confirmed_at, motivo: r.reason,
      valor_mensal: r.monthly_amount, origem_valor: r.price_source ? PRICE_SOURCE_LABEL[r.price_source] : null, valor_capturado_em: r.price_captured_at,
    }));
  },
};
